import { updateChatFormInputHeight } from "./updateChatFormInputHeight";
import { messageRequestEvent } from "../events/messageRequestEvent";
import { renderMessage } from "./renderMessage";
import { handleAjaxError } from "./handleAjaxError";
import { updateCredits } from "./updateCredits";
import { appendUserMessage } from "./appendUserMessage";
import { appendAdminMessage } from "./appendAdminMessage";
import { scrollToChatBottom } from "./scrollToChatBottom";
import { messageResponseEvent } from "../events/messageResponseEvent";
import { evaluateChatHeight } from "./evaluateChatHeight";
import { messageQueue } from "./messageQueue";
import { getTypingQueue } from "./typingAnimation";
import { hideMessageLoader } from "./hideMessageLoader";
import { createAnonSession } from "./createAnonSession";
import {
  setContentGeneratorProgress,
  startContentGeneratorProgressSimulation,
  stopContentGeneratorProgressSimulation,
  showGenerationSuccessAlert,
} from "../components/contentGeneratorModal";
import { startVideoPolling } from "./asyncVideoPolling";
import {
  appendGuestLocalHistory,
  buildGuestLocalUserEntry,
  discardGuestLocalCustomRoleplaySetup,
  getGuestLocalHistoryForRequest,
  migrateGuestLocalHistoryToSessionToken,
  shouldUseGuestLocalHistoryMode,
} from "../helpers/guestLocalHistory";
import {
  getDynamicResponseDelayMs,
  getMessageFrontendDelayCase,
  getResponsePostWaitMs,
  getResponsePreviewDelayMs,
} from "../helpers/frontendDelays";
import {
  ACCESS_REQUIREMENT_CODES,
  isAccessRequirementError,
} from "../helpers/accessRequirementErrors";
import { chatDebugLog } from "../helpers/chatDebug";
import { refreshConversationState } from "../components/conversationState";
const { markPromiseHandled } = require("../helpers/promiseSafety.cjs");
const {
  createGenerationRequestId,
  waitForAsyncTextGeneration,
} = require("../helpers/asyncTextGeneration.cjs");

const ROLEPLAY_SCENARIO_KEY =
  window.growtype_chat_ajax?.roleplay_scenario_key || "roleplay_scenario";

function createChatRequestError(response) {
  if (response instanceof Error) {
    return response;
  }

  const status = Number(response?.status || 0);
  const error = new Error(
    status ? `Chat request failed with HTTP ${status}.` : "Chat request failed."
  );
  error.status = status;
  error.statusText = response?.statusText || "";
  error.responseJSON = response?.responseJSON;
  error.responseText = response?.responseText;
  return error;
}

/**
 * @param message
 * @param chatForm
 */
export async function submitChatMessages(messages, chatForm) {
  const chatId = chatForm.attr("id");
  const contentWrapper = chatForm.find(".g-chatform-content");

  let sessionToken = chatForm.attr("data-session-token");
  const userToken = chatForm.attr("data-user-token");
  const responseMinimumSeconds = parseInt(
    chatForm.attr("data-response-min-time")
  );
  let submitBtn = chatForm.find(".g-chatform-footer-submit-wrapper .btn-send");
  let chatFormInput = jQuery(
    ".g-chatform-footer-input-wrapper .g-chatform-text-input"
  );
  const guestLocalHistoryEnabled = shouldUseGuestLocalHistoryMode();
  chatDebugLog('guestHistory', "[guest-history] submitChatMessages:start", {
    chatId,
    sessionToken,
    userTokenPrefix: userToken ? String(userToken).slice(0, 12) : "",
    guestLocalHistoryEnabled,
    messageCount: Object.keys(messages || {}).length,
  });
  let userMessageTemplate;
  // ── ZERO-WRITE: Defer virtual session materialization to backend ──────────
  // PENDING: virtual sessions are materialized in-flight during the single
  // growtype_chat_message_submit AJAX call below without an extra pre-flight HTTP request.

  /**
   *  Sanitize messages
   */
  let sanitizedMessages = {};
  let ajaxMessages = {};
  Object.entries(messages).forEach(([key, message]) => {
    let quizAnswers = Array.isArray(message["quiz_answers"])
      ? [...message["quiz_answers"]]
      : [];

    // Sanitize 'value'
    if (typeof message["value"] === "string") {
      message["value"] = message["value"].trim();
    } else if (
      quizAnswers.length > 0 &&
      typeof quizAnswers[0]?.["value"] === "string"
    ) {
      message["value"] = quizAnswers[0]["value"];
    }

    message["category"] =
      message["category"] ?? chatFormInput.attr("data-category") ?? "text";

    message["nsfw"] =
      message["nsfw"] ??
      chatFormInput.attr("data-nsfw") ??
      chatForm.find('input[name="nsfw_chat"]').is(":checked") ??
      false;

    /**
     * Push message to sanitized messages for UI processing
     */
    sanitizedMessages[key] = message;

    /**
     * Create a clean clone without DOM nodes for jQuery.ajax
     */
    const ajaxMessage = { ...message };
    delete ajaxMessage.userMessageTemplate;
    delete ajaxMessage.adminMessageTemplate;
    ajaxMessages[key] = ajaxMessage;
  });

  chatDebugLog('guestHistory', "[guest-history] submitChatMessages:sanitized", {
    sanitizedCount: Object.keys(sanitizedMessages).length,
    ajaxCount: Object.keys(ajaxMessages).length,
    categories: Object.values(sanitizedMessages).map(
      (message) => message.category || "text"
    ),
  });

  /**
   * Append preview chat messages
   */
  Object.entries(sanitizedMessages).forEach(function (
    [key, messageData],
    index
  ) {
    let messageGenerateStartTime;
    const frontendDelayCase = getMessageFrontendDelayCase(messageData);
    let responsePreviewDelay = getResponsePreviewDelayMs(
      chatForm,
      frontendDelayCase
    );
    let messageValue = messageData["value"];
    let giftId = messageData["gift_id"] ?? "";
    let skipQuestionPreview = messageData["skip_question_preview"];

    if (
      messageData?.quiz_answers &&
      Object.values(messageData.quiz_answers)?.[0]?.[0]
    ) {
      messageValue = Object.values(messageData.quiz_answers)[0][0];
    }

    const adminMessageTemplate = messageData["adminMessageTemplate"] ||
      (giftId.length > 0
        ? appendAdminMessage(chatForm, messageValue, skipQuestionPreview)
        : $());

    // If the debounce logic already instantly appended the user loader, use it.
    // Otherwise, fallback and append a new one.
    let userMessageTemplate = messageData["userMessageTemplate"];
    if (!userMessageTemplate) {
      userMessageTemplate = appendUserMessage(
        chatForm,
        messageData["category"],
        { reusePending: messageData.reuse_pending_loader !== false }
      );
    }

    if (giftId.length > 0) {
      adminMessageTemplate.find(".gift-wrapper").addClass("has-animation");

      setTimeout(function () {
        adminMessageTemplate
          .find(".gift-wrapper")
          .removeClass("has-animation");
      }, 3000);
    }

    /**
     * Dispatch event
     */
    document.dispatchEvent(
      messageRequestEvent({
        userMessageTemplate: userMessageTemplate,
        adminMessageTemplate: adminMessageTemplate,
        message: messageData,
      })
    );

    updateChatFormInputHeight({ reset: true });

    // Create a promise to track this specific AJAX request
    let ajaxResolve, ajaxReject;
    let activatedRoleplayRefreshPromise = null;
    const ajaxPromise = new Promise((resolve, reject) => {
      ajaxResolve = resolve;
      ajaxReject = reject;
    });
    // The request can fail while earlier queue work is still running. Attach a
    // handler now; the queue still awaits the original promise below.
    markPromiseHandled(ajaxPromise);
    const generationRequestId = createGenerationRequestId();

    const requestGenerationStatus = (job) =>
      new Promise((resolve, reject) => {
        jQuery.ajax({
          url: growtype_chat_ajax.url,
          type: "post",
          timeout: 15000,
          data: {
            action: growtype_chat_ajax.generation_job_status_action,
            job_id: job.id,
            job_token: job.token,
            nonce: growtype_chat_ajax.nonce,
          },
          success: resolve,
          error: (response) => reject(createChatRequestError(response)),
        });
      });

    jQuery.ajax({
      url: growtype_chat_ajax.url,
      type: "post",
      data: {
        action: growtype_chat_ajax.message_submit_action,
        user: {
          token: userToken,
          messages: ajaxMessages,
        },
        session: {
          token: sessionToken,
          url: window.location.href,
          bot_profile: window.growtype_chat[chatId]["bot_profile"],
          [ROLEPLAY_SCENARIO_KEY]:
            window.growtype_chat?.[chatId]?.[ROLEPLAY_SCENARIO_KEY] ||
            new URL(window.location.href).searchParams.get(ROLEPLAY_SCENARIO_KEY) ||
            "",
          anonymous_local_history_mode: guestLocalHistoryEnabled,
          local_history: guestLocalHistoryEnabled
            ? getGuestLocalHistoryForRequest(chatForm)
            : [],
        },
        generation_request_id: generationRequestId,
        nonce: growtype_chat_ajax.nonce,
      },
      beforeSend: function () {
        messageGenerateStartTime = Date.now();
        chatDebugLog('guestHistory', "[guest-history] submitChatMessages:beforeSend", {
          sessionToken,
          guestLocalHistoryEnabled,
          localHistoryCount: guestLocalHistoryEnabled
            ? getGuestLocalHistoryForRequest(chatForm).length
            : 0,
        });
      },
      complete: function () {
        evaluateChatHeight();
      },
      success: async function (response) {
        try {
          response = await waitForAsyncTextGeneration(response, {
            requestStatus: requestGenerationStatus,
          });
        } catch (error) {
          ajaxReject(error);
          return;
        }

        chatDebugLog('guestHistory', "[guest-history] submitChatMessages:ajax-success", {
          success: response?.success,
          hasSessionToken: !!response?.data?.session_token,
          localHistoryAppendCount:
            response?.data?.local_history_append?.length || 0,
          responseMessageCount: response?.data?.response_messages?.length || 0,
        });

        const questionEvaluation = response.data?.question_evaluation || {};
        const activatedRoleplayKey = String(
          questionEvaluation.roleplay_scenario_key || ""
        ).trim();
        const activatedRoleplayMode = String(
          questionEvaluation.roleplay_session_mode || ""
        ).trim();

        if (
          activatedRoleplayKey &&
          activatedRoleplayMode === "embedded"
        ) {
          activatedRoleplayRefreshPromise = refreshConversationState(
            chatForm,
            {
              session_token: response.data?.session_token || sessionToken,
              roleplay_scenario: activatedRoleplayKey,
              intro_quiz: "",
              roleplay_quiz_slug: "",
            },
            {
              animate: false,
              scrollToBottom: true,
            }
          );
        }

        ajaxResolve(response);
      },
      error: function (response) {
        chatDebugLog('guestHistory', "[guest-history] submitChatMessages:ajax-error", {
          status: response?.status,
          errorCode: response?.responseJSON?.data?.code || null,
        });
        ajaxReject(createChatRequestError(response));
      },
    });

    const isGenerating = userMessageTemplate.hasClass(
      "g-chatform-message-generating"
    );

    if (isGenerating) {
      startContentGeneratorProgressSimulation(userMessageTemplate);
    }

    // Add a task to the global message queue to handle this response in order
    messageQueue.addToQueue(async () => {
      if (
        userMessageTemplate &&
        userMessageTemplate.length &&
        !userMessageTemplate.closest("body").length
      ) {
        contentWrapper.append(userMessageTemplate);
      }

      // Wait for any existing typing to fully complete before showing the response loader
      await getTypingQueue();

      // Show the loader bubble only when this task starts processing
      userMessageTemplate.show();
      scrollToChatBottom(500, true);

      try {
        const response = await ajaxPromise;
        chatDebugLog('guestHistory', "[guest-history] submitChatMessages:queue-response", {
          success: response?.success,
          hasData: !!response?.data,
        });

        if (response.data?.session_token) {
          migrateGuestLocalHistoryToSessionToken(
            chatForm,
            response.data.session_token
          );
          chatForm.attr("data-session-token", response.data.session_token);
          sessionToken = response.data.session_token;
          if (window.growtype_chat && window.growtype_chat[chatId]) {
            window.growtype_chat[chatId]["session_token"] = response.data.session_token;
          }
        }

        const isAsyncVideo =
          response.success !== false && response.data?.is_async === true;

        // Async video keeps the existing simulation visible at 99% while its
        // polling flow waits for the generated asset.
        if (isGenerating && !isAsyncVideo) {
          setContentGeneratorProgress(userMessageTemplate, 100);
          setTimeout(() => {
            stopContentGeneratorProgressSimulation(userMessageTemplate);
            showGenerationSuccessAlert();
          }, 500);
        }

        if (response.success === false) {
          console.warn("submitChatMessages: Response success is false");
          handleAjaxError(response, submitBtn);

          if (
            response.data &&
            isAccessRequirementError(response.data.message) &&
            response.data.message !== ACCESS_REQUIREMENT_CODES.PROFILE_REQUIRED
          ) {
            adminMessageTemplate.remove();
            userMessageTemplate.remove();
            evaluateChatHeight();
          }
          return;
        }

        if (
          typeof response["data"] === "undefined" ||
          typeof response["data"]["question_message_id"] === "undefined"
        ) {
          // alert('sorry, please try again');
        } else {
          const questionMessageTemplate = adminMessageTemplate.length
            ? adminMessageTemplate
            : userMessageTemplate;
          questionMessageTemplate.attr(
            "data-id",
            response["data"]["question_message_id"]
          );
          questionMessageTemplate
            .find('.g-chatform-message-edit, .g-chatform-message-continue')
            .removeClass("d-none");
        }

        /**
         * Dispatch event as soon as response is received (e.g. for roleplay/mood updates)
         * This ensures the header updates BEFORE the typing animation starts.
         */
        document.dispatchEvent(
          messageResponseEvent({
            response: response,
          })
        );

        if (guestLocalHistoryEnabled) {
          if (response.data?.question_evaluation?.roleplay_scenario_key === "custom") {
            // A completed setup form must not be replayed as a new request on reload.
            discardGuestLocalCustomRoleplaySetup(chatForm);
          }

          const localEntries = [];
          const userEntry = messageData.skip_local_history
            ? null
            : buildGuestLocalUserEntry(messageData);

          if (userEntry) {
            localEntries.push(userEntry);
          }

          (response.data?.local_history_append || []).forEach((entry) => {
            if (entry && typeof entry === "object") {
              localEntries.push(entry);
            }
          });

          chatDebugLog('guestHistory', "[guest-history] submitChatMessages:local-history-append", {
            localEntriesCount: localEntries.length,
            roles: localEntries.map((entry) => entry.role),
          });

          if (localEntries.length > 0) {
            appendGuestLocalHistory(chatForm, localEntries);
          }
        }

        if (activatedRoleplayRefreshPromise) {
          try {
            await activatedRoleplayRefreshPromise;
            updateCredits(response.data.existing_credits);
            return;
          } catch (refreshError) {
            console.error(
              "submitChatMessages: Could not refresh the activated roleplay transcript",
              refreshError
            );
          }
        }

        const timeLeft = getMessagePreviewDelayTime(
          chatForm,
          frontendDelayCase,
          messageGenerateStartTime,
          responsePreviewDelay,
          response
        );
        console.log(
          "[Video] Queue: timeLeft:",
          timeLeft,
          "total wait:",
          timeLeft + getResponsePostWaitMs(chatForm, frontendDelayCase)
        );

        await new Promise((resolve) =>
          setTimeout(
            resolve,
            timeLeft + getResponsePostWaitMs(chatForm, frontendDelayCase)
          )
        );

        if (isAsyncVideo && response.data.generation_id) {
          const genId = response.data.generation_id;
          const generationMessageId = response.data.generation_message_id || "";

          if (generationMessageId) {
            userMessageTemplate.attr("data-id", generationMessageId);
          }

          chatDebugLog('videoPoll', "[AsyncVideo:submit] isAsyncVideo=true genId:", genId, "category:", messageData.category);

          // Determine whether the async video is locked (requires credits to unlock)
          // by checking if the server-rendered message includes an unlock button.
          const videoMsg = (response.data.response_messages || []).find(
            (msg) => (msg.html || "").includes(`data-generation-id="${genId}"`)
          );
          const isLockedVideo = videoMsg && (videoMsg.html || "").includes("btn-unlock-content");

          chatDebugLog('videoPoll', "[AsyncVideo:submit] videoMsg found:", !!videoMsg, "isLockedVideo:", isLockedVideo);

          if (isLockedVideo) {
            // Explicit video request (category === 'video'): auto-unlock so the user
            // sees the generating loader immediately without a second "Unlock Video" click.
            // For spontaneous/offer videos (other categories) show the blurred card normally.
            if (messageData.category === 'video' && videoMsg) {
              chatDebugLog('videoPoll', "[AsyncVideo:submit] category=video → auto-unlock + poll");
              // Parse unlock params from the server-rendered button
              const $videoHtml = $($.trim(videoMsg.html));
              const $unlockBtn = $videoHtml.find('.btn-unlock-content');
              const msgIdEnc   = $unlockBtn.attr('data-message-id') || '';
              const creditsCost = $unlockBtn.attr('data-credits-cost') || 0;
              const xpAddedVal  = $unlockBtn.attr('data-xp-added') || 0;

              chatDebugLog('videoPoll', "[AsyncVideo:submit] auto-unlock params → msgId:", msgIdEnc, "credits:", creditsCost, "xp:", xpAddedVal);

              // Keep the loader visible while the server confirms the paid
              // unlock. Polling starts only after that durable unlock succeeds.
              userMessageTemplate.removeClass("is-pending-response g-chatform-message-generating");

              jQuery.ajax({
                url: growtype_chat_ajax.url,
                type: 'post',
                data: {
                  action: 'growtype_chat_unlock_content',
                  asset_url: 'generate_pending',
                  credits_cost: creditsCost,
                  xp_added: xpAddedVal,
                  message_id: msgIdEnc,
                  session_token: chatForm.attr('data-session-token'),
                  nonce: growtype_chat_ajax.nonce,
                },
                success: function (unlockResponse) {
                  chatDebugLog('videoPoll', "[AsyncVideo:submit] auto-unlock response:", unlockResponse.success, unlockResponse.data);
                  if (unlockResponse.success) {
                    updateCredits(unlockResponse.data.new_balance);
                    startVideoPolling(genId, userMessageTemplate);
                  } else {
                    console.error("[AsyncVideo:submit] auto-unlock FAILED:", unlockResponse);
                    stopContentGeneratorProgressSimulation(userMessageTemplate);
                    renderMessage(chatForm, {
                      data: Object.assign({}, response.data, {
                        response_messages: [videoMsg],
                      }),
                    }, userMessageTemplate);
                  }
                },
                error: function (xhr, status, err) {
                  console.error("[AsyncVideo:submit] auto-unlock AJAX error:", status, err, xhr.responseText);
                  stopContentGeneratorProgressSimulation(userMessageTemplate);
                  renderMessage(chatForm, {
                    data: Object.assign({}, response.data, {
                      response_messages: [videoMsg],
                    }),
                  }, userMessageTemplate);
                },
              });

              // Render text companion messages only (strip the locked video card)
              const strippedForVideo = Object.assign({}, response, {
                data: Object.assign({}, response.data, {
                  response_messages: (response.data.response_messages || []).filter(
                    (msg) => !(msg.html || '').includes(`data-generation-id="${genId}"`)
                  ),
                }),
              });
              if (strippedForVideo.data.response_messages.length > 0) {
                await renderMessage(chatForm, strippedForVideo, null);
              }
            } else {
              // Spontaneous / offer locked video: show the blurred card, let user decide.
              // Do NOT dispatch growtypeChatVideoPendingScan — resumePendingVideoGenerationsFromDom
              // would start polling before the user pays.
              chatDebugLog('videoPoll', "[AsyncVideo:submit] spontaneous locked video → blurred card shown, waiting for user unlock");
              await renderMessage(chatForm, response, userMessageTemplate);
            }

          } else {
            // Free / auto async video: keep the placeholder visible as a generating loader
            // and poll until the video is ready.
            chatDebugLog('videoPoll', "[AsyncVideo:submit] free async video → polling on placeholder");
            userMessageTemplate.removeClass("is-pending-response g-chatform-message-generating");
            userMessageTemplate.attr("data-generation-id", genId);

            // Start polling — on completion updateMessageWithVideo will replace the
            // assets-wrapper content (the generating loader) with the real video.
            startVideoPolling(genId, userMessageTemplate);

            // Build a stripped response that excludes the video message (index 0)
            // so renderMessage only renders the text companion messages.
            const strippedResponse = Object.assign({}, response, {
              data: Object.assign({}, response.data, {
                response_messages: (response.data.response_messages || []).filter(
                  (msg) => {
                    // Keep messages that are NOT the video asset message
                    const html = msg.html || "";
                    return !html.includes(`data-generation-id="${genId}"`);
                  }
                ),
              }),
            });

            chatDebugLog('videoPoll', 
              "[AsyncVideo] Stripped response_messages count:",
              strippedResponse.data.response_messages.length,
              "(original:",
              (response.data.response_messages || []).length,
              ")"
            );

            // Render text companion messages without replacing userMessageTemplate
            if (strippedResponse.data.response_messages.length > 0) {
              await renderMessage(chatForm, strippedResponse, null);
            }
          }
        } else {
          // Non-async: normal flow — await full render then trigger DOM scan
          chatDebugLog('videoPoll', "[AsyncVideo:submit] non-async → normal renderMessage");
          await renderMessage(chatForm, response, userMessageTemplate);

          document.dispatchEvent(new CustomEvent("growtypeChatVideoPendingScan"));
        }

        /**
         * Update credits
         */
        updateCredits(response.data.existing_credits);
      } catch (response) {
        console.error("submitChatMessages: Error in queue task", {
          message: response?.message || "Chat request failed.",
          status: response?.status || 0,
        });
        chatDebugLog('guestHistory', "[guest-history] submitChatMessages:queue-error", {
          status: response?.status,
          errorCode: response?.responseJSON?.data?.code || null,
        });
        const timeLeft = getMessagePreviewDelayTime(
          chatForm,
          frontendDelayCase,
          messageGenerateStartTime,
          responsePreviewDelay,
          null
        );

        if (isGenerating) {
          stopContentGeneratorProgressSimulation(userMessageTemplate);
        }

        handleAjaxError(response, submitBtn);

        const responseJSON = response.responseJSON;
        if (
          responseJSON &&
          responseJSON.data &&
          isAccessRequirementError(responseJSON.data.message)
        ) {
          adminMessageTemplate.remove();
          userMessageTemplate.remove();
          evaluateChatHeight();
        } else {
          // Wait before showing the error
          await new Promise((resolve) =>
            setTimeout(
              resolve,
              timeLeft + getResponsePostWaitMs(chatForm, frontendDelayCase)
            )
          );

          // Determine a human-readable error message
          const isTimeout =
            response.status === 504 ||
            response.status === 408 ||
            response.statusText === "timeout";
          const errorMessage = isTimeout
            ? "⏳ Generation is taking longer than expected. Please try again in a moment."
            : responseJSON?.data?.message ||
            "⚠️ Something went wrong. Please try again.";

          // Replace the loader bubble with an inline error message
          hideMessageLoader(userMessageTemplate);
          userMessageTemplate
            .find(".g-chatform-message-content-inner")
            .html(
              `<p class="text-danger" style="margin:0;">${errorMessage}</p>`
            )
            .show();

          evaluateChatHeight();

          // Auto-remove error bubble after 6s
          setTimeout(() => {
            userMessageTemplate.fadeOut(400, () => {
              userMessageTemplate.remove();
              evaluateChatHeight();
            });
          }, 6000);
        }
      }
    });
  });
}

function getMessagePreviewDelayTime(
  chatForm,
  frontendDelayCase,
  messageGenerateStartTime,
  responsePreviewDelay = 0,
  response = null
) {
  let messageGenerateEndTime = Date.now();
  let timeTaken = messageGenerateEndTime - messageGenerateStartTime;
  let dynamicDelay = getDynamicResponseDelayMs(
    chatForm,
    frontendDelayCase,
    response
  );

  let finalDelay = Math.max(responsePreviewDelay, dynamicDelay);
  let timeLeft = finalDelay - timeTaken;

  return timeLeft > 0 ? timeLeft : 0;
}
